import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Film, Loader2, Image as ImageIcon, Download, Sparkles,
  Play, Package, RefreshCw, ExternalLink,
} from "lucide-react";

/**
 * Cinematic Ads admin tab — visual picker + one-click campaign pack.
 *
 * Three flows:
 *  1. Pick a single template → "Generate" → preview + copy strings
 *  2. "Generate all 8" → batch run, grid of thumbs
 *  3. "Download Campaign Pack ZIP" → bundles 8 photos + value stack +
 *      MP4 + copy.txt + manifest.json (one-click ad-ready bundle)
 *
 * Why batch + zip in one click: a campaign is 8 photos + 1 video +
 * 1 pricing slide + a 1-pager of headline/CTA strings. Doing each
 * separately wastes 5+ minutes of admin time per launch.
 */

function adminHeaders() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return u.email ? { "X-User-Email": u.email } : {};
  } catch {
    return {};
  }
}

const ASPECTS = ["1.91:1", "1:1", "9:16", "4:5"];

export default function AdminCinematicAdsTab() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingId, setGeneratingId] = useState(null);
  const [results, setResults] = useState({}); // {template_id: {url, copy, mime_type}}
  const [valueStack, setValueStack] = useState(null);
  const [videoResult, setVideoResult] = useState(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [packDownloading, setPackDownloading] = useState(false);
  const [fiveAdsDownloading, setFiveAdsDownloading] = useState(false);
  const [aspect, setAspect] = useState("1:1");

  useEffect(() => {
    axios.get(`${API}/cinematic-ads/templates`)
      .then((r) => setTemplates(r.data?.templates || []))
      .catch((e) => console.error("templates fetch failed", e))
      .finally(() => setLoading(false));
  }, []);

  const generateOne = useCallback(async (template_id) => {
    setGeneratingId(template_id);
    try {
      const r = await axios.post(
        `${API}/cinematic-ads/generate`,
        { template_id, aspect },
        { headers: adminHeaders() },
      );
      setResults((prev) => ({ ...prev, [template_id]: r.data }));
    } catch (e) {
      console.error("generate failed", e);
    }
    setGeneratingId(null);
  }, [aspect]);

  const generateBatch = useCallback(async () => {
    setBatchRunning(true);
    try {
      const r = await axios.post(
        `${API}/cinematic-ads/generate-batch`,
        {},
        { headers: adminHeaders() },
      );
      const next = {};
      for (const item of r.data?.results || []) {
        if (item.status === "success") next[item.template_id] = item;
      }
      setResults((prev) => ({ ...prev, ...next }));
    } catch (e) {
      console.error("batch failed", e);
    }
    setBatchRunning(false);
  }, []);

  const renderValueStack = useCallback(async () => {
    try {
      const r = await axios.post(
        `${API}/cinematic-ads/render-value-stack`,
        { aspect },
        { headers: adminHeaders() },
      );
      setValueStack(r.data);
    } catch (e) {
      console.error("value stack failed", e);
    }
  }, [aspect]);

  const renderVideo = useCallback(async () => {
    const reframes = Object.entries(results)
      .filter(([k]) => k.startsWith("sk_reframe_"))
      .slice(0, 4)
      .map(([k, v]) => ({
        path: v.url,
        headline: (v.copy || {}).headline_sk || "",
        duration: 2.0,
      }));
    if (reframes.length < 2) return;
    if (valueStack) {
      reframes.push({
        path: valueStack.url,
        headline: "BLACK FRIDAY · Získaj prístup teraz",
        duration: 2.5,
      });
    }
    try {
      const r = await axios.post(
        `${API}/cinematic-ads/render-video`,
        { frames: reframes, aspect: "1:1", per_frame_seconds: 2.0 },
        { headers: adminHeaders() },
      );
      setVideoResult(r.data);
    } catch (e) {
      console.error("video render failed", e);
    }
  }, [results, valueStack]);

  const downloadCampaignPack = useCallback(async () => {
    setPackDownloading(true);
    try {
      const r = await axios.post(
        `${API}/cinematic-ads/campaign-pack`,
        {},
        { headers: adminHeaders(), responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "toprankpulse_campaign_pack.zip";
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("campaign pack failed", e);
    }
    setPackDownloading(false);
  }, []);

  const downloadDeployBPack = useCallback(async () => {
    // Separate from the campaign pack — this is the env+webhook setup
    // kit used once when spinning up a sibling brand. Lives in
    // /app/memory/deploy_b/ on the server.
    try {
      const r = await axios.get(
        `${API}/admin/deploy-b-package`,
        { headers: adminHeaders(), responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "toprankpulse_deploy_b_package.zip";
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("deploy_b pack failed", e);
    }
  }, []);

  const downloadFiveAdsPack = useCallback(async () => {
    // Focused 5-pack — the hand-picked best ads for FB Ads Manager
    // drag-drop. Faster than the full campaign-pack (no value-stack,
    // no video stitching) and the templates are pre-curated for max
    // angle diversity (price anchor, social proof, success story,
    // controversy, cost reframe).
    setFiveAdsDownloading(true);
    try {
      const r = await axios.post(
        `${API}/cinematic-ads/download-pack`,
        {
          template_ids: [
            "sk_cinematic_receipt_strikethrough",
            "sk_cinematic_phone_proof",
            "sk_cinematic_laptop_dashboard",
            "sk_reframe_seo_typewriter",
            "sk_reframe_audit_cheap",
          ],
          languages: ["en", "sk"],
        },
        { headers: adminHeaders(), responseType: "blob", timeout: 180_000 },
      );
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.download = `toprankpulse-ad-pack-${today}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("5-ads pack failed", e);
    }
    setFiveAdsDownloading(false);
  }, []);

  if (loading) {
    return (
      <div className="admin-root p-6 text-sm text-gray-500" data-testid="cinematic-ads-loading">
        Načítavam šablóny…
      </div>
    );
  }

  return (
    <div className="admin-root space-y-6" data-testid="cinematic-ads-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
            <Film className="w-5 h-5 text-violet-600" />
            Cinematic Ads — FB Campaign Studio
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            Generuj cinematic photo creatives (Nano Banana) + Hormozi-style value-stack pricing slide + MP4 carousel.
            Stiahni ZIP s 8 photos + video + copy.txt a uploadni do FB Ads Manager.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {ASPECTS.map((a) => (
              <button
                key={a}
                type="button"
                onClick={() => setAspect(a)}
                data-testid={`cinematic-aspect-${a.replace(/[:.]/g, "")}`}
                className={`px-3 py-1.5 rounded text-xs font-bold ${
                  aspect === a ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-200"
                }`}
              >
                {a}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={generateBatch}
            disabled={batchRunning}
            data-testid="cinematic-generate-batch"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-bold hover:bg-violet-700 disabled:opacity-60"
          >
            {batchRunning
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Sparkles className="w-4 h-4" />}
            Generovať všetkých 8
          </button>
          <button
            type="button"
            onClick={renderValueStack}
            data-testid="cinematic-value-stack"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 text-white text-sm font-bold hover:bg-amber-600"
          >
            <Package className="w-4 h-4" />
            Pricing slide
          </button>
          <button
            type="button"
            onClick={renderVideo}
            disabled={Object.keys(results).filter((k) => k.startsWith("sk_reframe_")).length < 2}
            data-testid="cinematic-render-video"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-600 text-white text-sm font-bold hover:bg-rose-700 disabled:opacity-50"
          >
            <Film className="w-4 h-4" />
            Render MP4
          </button>
          <button
            type="button"
            onClick={downloadFiveAdsPack}
            disabled={fiveAdsDownloading}
            data-testid="cinematic-fb-5pack"
            title="Stiahne 5 hand-picked ad variantov pre FB Ads Manager (drag-drop ready)"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 text-gray-900 text-sm font-bold hover:bg-amber-400 disabled:opacity-60"
          >
            {fiveAdsDownloading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Download className="w-4 h-4" />}
            FB Ads 5-Pack
          </button>
          <button
            type="button"
            onClick={downloadCampaignPack}
            disabled={packDownloading}
            data-testid="cinematic-campaign-pack"
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-60"
          >
            {packDownloading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <Download className="w-4 h-4" />}
            Stiahnuť Campaign Pack ZIP
          </button>
          <button
            type="button"
            onClick={downloadDeployBPack}
            data-testid="deploy-b-pack"
            title="Setup kit pre paralelný druhý deploy: env šablóny, VAPID generator, Stripe Dashboard kroky"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700 text-white text-sm font-bold hover:bg-slate-800"
          >
            <Download className="w-4 h-4" />
            Deploy-B Setup Kit
          </button>
        </div>
      </div>

      {/* Templates grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {templates.map((t) => {
          const r = results[t.id];
          const isGenerating = generatingId === t.id;
          return (
            <div
              key={t.id}
              data-testid={`cinematic-card-${t.id}`}
              className="bg-white border border-gray-200 rounded-xl overflow-hidden flex flex-col"
            >
              <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
                {r?.url ? (
                  <img
                    src={`${API}${r.url}`}
                    alt={t.headline_sk}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon className="w-12 h-12 text-gray-300" />
                )}
                {isGenerating && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                  </div>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 font-mono">
                  {t.id}
                </p>
                <h3 className="font-bold text-sm text-gray-900 mt-1 leading-tight">
                  {t.headline_sk}
                </h3>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{t.description}</p>
                <div className="mt-auto pt-3 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => generateOne(t.id)}
                    disabled={isGenerating}
                    data-testid={`cinematic-gen-${t.id}`}
                    className="flex-1 px-3 py-2 rounded-lg bg-gray-900 text-white text-xs font-bold hover:bg-gray-800 disabled:opacity-60 flex items-center justify-center gap-1.5"
                  >
                    {isGenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    {r?.url ? "Re-gen" : "Generovať"}
                  </button>
                  {r?.url && (
                    <a
                      href={`${API}${r.url}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`cinematic-open-${t.id}`}
                      className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Value stack preview */}
      {valueStack?.url && (
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="cinematic-value-stack-preview">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
              <Package className="w-4 h-4 text-amber-500" />
              Tower-of-Value Pricing Slide
            </h3>
            <a
              href={`${API}${valueStack.url}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-violet-600 hover:text-violet-800"
            >
              Otvoriť v novej karte ↗
            </a>
          </div>
          <img
            src={`${API}${valueStack.url}`}
            alt="Value stack"
            className="w-full max-w-3xl border border-gray-200 rounded-lg"
          />
        </div>
      )}

      {/* Video preview */}
      {videoResult?.url && (
        <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid="cinematic-video-preview">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
              <Play className="w-4 h-4 text-rose-500" />
              Campaign Video — {videoResult.frames} frames · {videoResult.approx_duration_sec}s
            </h3>
            <a
              href={`${API}${videoResult.url}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-bold text-violet-600 hover:text-violet-800"
            >
              Stiahnuť MP4 ↗
            </a>
          </div>
          <video
            src={`${API}${videoResult.url}`}
            controls
            autoPlay
            muted
            loop
            className="w-full max-w-md border border-gray-200 rounded-lg"
          />
        </div>
      )}
    </div>
  );
}
